import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SyncLoader } from "react-spinners";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Container, FormControl, InputAdornment, InputLabel, MenuItem, Stack, TextField } from "@mui/material";
import Select from "@mui/material/Select";
import { type SelectChangeEvent } from "@mui/material/Select";
import { useTheme } from "@mui/material/styles";

import useSegmentTrackEvent from "../../../utils/useSegmentTrackEvent";
import { useGKEAndCloudConnect } from "../../K8s/hooks";
import { ALL_CATEGORIES, ALL_CLOUD_PROVIDERS } from "../constants";
import { useInsightsContext } from "../context";
import {
  useAWSAccountsWithMissingPermissions,
  useInsightPermissions,
  useInsights,
  useReceivingSecurityHubInsights,
  useSecurityHubInsightPermissions,
} from "../hooks";
import { NotFound } from "../NotFound/NotFound";
import {
  type AddPermissionsEventType,
  type BreakdownData,
  type CloudProviderTag,
  DisplayStatus,
  type Filters,
  type Insight,
  InsightAccessType,
  InsightPriority,
  InsightProviderId,
  type InsightsAddPermissionsProperties,
  type InsightsListViewedProperties,
  InsightsSortOrder,
} from "../types";
import { calculatePermissionsMetrics, calculateTotalSavingsForTimePeriod, getInsightId, sortInsights } from "../utils";
import { type FilterConfig } from "./FilterSection";
import FilterSectionsList from "./FilterSectionsList";
import Header from "./Header";
import { InsightsListRow } from "./InsightsListRow";
import PermissionsSummaryModal from "./PermissionsSummaryModal/PermissionsSummaryModal";

const capitalizeProvider = (provider: string): string => {
  const capitalizationMap: Record<string, string> = {
    aws: "AWS",
    gcp: "Google Cloud",
    azure: "Azure",
    snowflake: "Snowflake",
  };
  return capitalizationMap[provider] || "";
};

const getAllTags = (insights: Insight[]): string[] => {
  const allTags = new Set<string>();

  insights.forEach((insight) => {
    if (insight.tags) {
      insight.tags.forEach((tag) => allTags.add(tag));
    }
  });

  return Array.from(allTags);
};

type SortListItem = { label: string; value: InsightsSortOrder };

export const SORT_LIST: SortListItem[] = [
  { label: "Priority (High to Low)", value: InsightsSortOrder.PRIORITY_DESCENDING },
  { label: "Priority (Low to High)", value: InsightsSortOrder.PRIORITY_ASCENDING },
  { label: "Value (High to Low)", value: InsightsSortOrder.SAVINGS_DESCENDING },
  { label: "Value (Low to High)", value: InsightsSortOrder.SAVINGS_ASCENDING },
];

type Props = {
  hideTitle?: boolean;
  hideFilters?: boolean;
};

export const InsightsList: React.FC<Props> = ({ hideTitle, hideFilters }) => {
  const insightListItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const listViewedEventTracked = useRef(false);
  const appcuesActionExecuted = useRef(false);

  const theme = useTheme();

  const { insights, isLoading } = useInsights();

  // TODO: Call this hook directly in PermissionsSummaryModal when gkeAccessType isn't needed in useInsightPermissions anymore
  const {
    hasGKE,
    accessType: gkeAccessType,
    nonHealthyOrganizationCloudConnectsIds,
    hasMissingCloudConnectPermissions,
    isLoading: isLoadingCloudConnects,
  } = useGKEAndCloudConnect();

  const { missingPermissionsInsights, noAccessInsights } = useInsightPermissions(gkeAccessType, insights);

  // TODO: Remove this hook when we start receiving security-hub insights
  const receivingSecurityInsights = useReceivingSecurityHubInsights();

  const {
    isLoading: isLoadingMissingPermissionAccounts,
    error: errorMissingPermissionAccounts,
    trustedAdvisorAccounts: accountsWithoutTAPermissions,
    costOptimizationHubAccounts: accountsWithoutCostOptPermissions,
    securityHubAccounts: accountsWithoutSecurityHubPermissions,
  } = useAWSAccountsWithMissingPermissions(receivingSecurityInsights);

  // TODO: Remove this hook when we start receiving security-hub insights
  const { isLoading: isLoadingSecurityInsights, insightsNum: tempSecurityHubInsightsNum } =
    useSecurityHubInsightPermissions(
      receivingSecurityInsights,
      accountsWithoutSecurityHubPermissions.length > 0,
      isLoadingMissingPermissionAccounts
    );

  const { trackEvent } = useSegmentTrackEvent({ allowNullUser: false });
  const {
    lastSelectedInsightId,
    scrolledToLastSelectedInsight,
    setScrolledToLastSelectedInsight,
    filters,
    setFilters,
    sortOrder,
    setSortOrder,
    searchTerm,
    setSearchTerm,
    hasAppcuesCloudFlowAnnouncement,
  } = useInsightsContext();

  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [expandedFilterSections, setExpandedFilterSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setIsFirstRender(false);
  }, []);

  useEffect(() => {
    if (insights?.length && lastSelectedInsightId && !scrolledToLastSelectedInsight) {
      const element = insightListItemRefs.current[lastSelectedInsightId];
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        setScrolledToLastSelectedInsight(true);
      }
    }
  }, [insights, lastSelectedInsightId, scrolledToLastSelectedInsight, setScrolledToLastSelectedInsight]);

  const changeSortOrder = (event: SelectChangeEvent) => {
    const newValue = event.target.value as InsightsSortOrder;

    setSortOrder(newValue);
  };

  const handleFilterChange = useCallback(
    (category: keyof Filters, value: string) => {
      setFilters((prev: Filters) => {
        const prevValues = prev[category] as string[];
        const index = prevValues.indexOf(value);
        const newValues = index === -1 ? [...prevValues, value] : prevValues.filter((item) => item !== value);
        return {
          ...prev,
          [category]: newValues,
        };
      });
    },
    [setFilters]
  );

  // TODO: Remove after Appcues modal has served it's purpose: [CMP-29055]
  useEffect(() => {
    if (!hasAppcuesCloudFlowAnnouncement || !insights?.length) return;

    if (!appcuesActionExecuted.current) {
      setExpandedFilterSections({
        Status: false,
        Priority: false,
        Categories: false,
        Provider: false,
        Tags: false,
        "Additional options": true,
      });
      handleFilterChange("additionalOptions", "With CloudFlows");
      appcuesActionExecuted.current = true;
    }
  }, [hasAppcuesCloudFlowAnnouncement, insights, handleFilterChange]);

  const toUpperCaseFirst = (str: string | null | undefined): string => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const FILTER_CONFIGS = useMemo<FilterConfig[]>(
    () => [
      {
        title: "Status",
        items: Object.values(DisplayStatus).map((item) => toUpperCaseFirst(item)),
        filterKey: "status",
        getLabel: (item) => item,
        filterFunction: (insight, item) => toUpperCaseFirst(insight.displayStatus) === item,
        isExpandedByDefault: true,
      },
      {
        title: "Priority",
        items: Object.values(InsightPriority),
        filterKey: "priority",
        getLabel: (item) => item,
        filterFunction: (insight, item) => insight.priority === item,
        isExpandedByDefault: true,
      },
      {
        title: "Categories",
        items: ALL_CATEGORIES,
        filterKey: "categories",
        getLabel: (item) => item,
        filterFunction: (insight, item) =>
          insight.categories?.some((cat) => cat.toLowerCase() === item.toLowerCase()) ?? false,
        isExpandedByDefault: true,
      },
      {
        title: "Provider",
        items: ALL_CLOUD_PROVIDERS,
        filterKey: "providers",
        getLabel: capitalizeProvider,
        filterFunction: (insight, item) => insight.cloudTags.includes(item as CloudProviderTag),
        isExpandedByDefault: !!filters.providers.length,
      },
      {
        title: "Tags",
        items: getAllTags(insights || []),
        filterKey: "tags",
        getLabel: (item) => item,
        filterFunction: (insight, item) => insight.tags?.includes(item) ?? false,
        isExpandedByDefault: !!filters.tags.length,
      },
      {
        title: "Additional options",
        items: ["Easy wins", "With CloudFlows"],
        filterKey: "additionalOptions",
        getLabel: (item) => item,
        filterFunction: (insight, item) => {
          if (item === "Easy wins") {
            return !!insight.easyWinDescription;
          }
          if (item === "With CloudFlows") {
            return !!insight.cloudFlowBlueprintId;
          }
          return false;
        },
        isExpandedByDefault: !!filters.additionalOptions.length,
      },
    ],
    [insights, filters]
  );

  const filteredInsights = insights || [];

  const allInsights = [...filteredInsights];

  const sortedInsights = sortInsights(allInsights, sortOrder);

  const filterInsight = useCallback(
    (insight: Insight) =>
      FILTER_CONFIGS.every(({ filterKey, filterFunction }) => {
        const currentFilterValues = filters[filterKey].map(String);
        if (currentFilterValues.length === 0) return true;
        return currentFilterValues.some((item) => filterFunction(insight, item));
      }),
    [FILTER_CONFIGS, filters]
  );

  const selectedInsightsNoFilter: Insight[] = useMemo(() => {
    const selected = sortedInsights.filter((insight) => {
      const searchText = searchTerm.toLowerCase();
      return (
        insight.title.toLowerCase().includes(searchText) ||
        insight.shortDescription.toLowerCase().includes(searchText) ||
        insight.detailedDescriptionMdx.toLowerCase().includes(searchText)
      );
    });

    return selected;
  }, [searchTerm, sortedInsights]);

  const selectedInsights: Insight[] = useMemo(
    () => selectedInsightsNoFilter.filter(filterInsight),
    [selectedInsightsNoFilter, filterInsight]
  );

  const events = useMemo<{
    listViewedEvent: InsightsListViewedProperties;
    addTAPermissionClickedEvent: InsightsAddPermissionsProperties;
    addCostOptPermissionClickedEvent: InsightsAddPermissionsProperties;
    addSecurityHubPermissionClickedEvent: InsightsAddPermissionsProperties;
    addGCPRecommenderPermissionClickedEvent: InsightsAddPermissionsProperties;
  }>(() => {
    const insightsArray = selectedInsights;
    const totalSavings = calculateTotalSavingsForTimePeriod(insightsArray, "yearly");

    const insightsHighestSavings = insightsArray.reduce((max, insight) => {
      const dailySavings =
        insight.results?.potentialDailySavings?.breakdown?.data.reduce(
          (sum: number, dataItem: BreakdownData) => sum + dataItem.value,
          0
        ) || 0;
      const totalSaving = dailySavings * 365;
      return Math.max(max, totalSaving);
    }, 0);

    const customInsightsCount = insightsArray.filter((insight) => insight.providerId === "custom").length;
    const cloudflowInsightsWithSavingsCount = insightsArray.filter((insight) => {
      const savings = calculateTotalSavingsForTimePeriod([insight], "monthly");
      const hasBlueprint = Boolean(insight.cloudFlowBlueprintId);
      return hasBlueprint && savings >= 100;
    }).length;

    const baseEventProperties = {
      pageType: "Governance",
      pageSubType: "Insights",
      feature: "Insights",
      permissionsNeededInsights: noAccessInsights.length,
      selectedInsightsCount: insightsArray.length,
      customInsightsCount,
      insightsTotalSavings: totalSavings.toString(),
      insightsHighestSavings,
      insightsSavingsPeriod: "yearly",
    };

    return {
      listViewedEvent: {
        ...baseEventProperties,
        isPageOpenEvent: isFirstRender,
        insightsSortOrder: sortOrder,
        insightsFiltersSelected: [...filters.providers, ...filters.categories],
        insightsSearchTerms: searchTerm,
        cloudflowInsightsWithSavingsCount,
      },
      addTAPermissionClickedEvent: {
        ...baseEventProperties,
        isPageOpenEvent: false,
        accountsMissingPermissions: accountsWithoutTAPermissions,
      },
      addCostOptPermissionClickedEvent: {
        ...baseEventProperties,
        isPageOpenEvent: false,
        accountsMissingPermissions: accountsWithoutCostOptPermissions,
      },
      addSecurityHubPermissionClickedEvent: {
        ...baseEventProperties,
        isPageOpenEvent: false,
        accountsMissingPermissions: accountsWithoutSecurityHubPermissions,
      },
      addGCPRecommenderPermissionClickedEvent: {
        ...baseEventProperties,
        isPageOpenEvent: false,
        accountsMissingPermissions: [],
      },
    };
  }, [
    isFirstRender,
    sortOrder,
    filters,
    searchTerm,
    selectedInsights,
    noAccessInsights,
    accountsWithoutTAPermissions,
    accountsWithoutCostOptPermissions,
    accountsWithoutSecurityHubPermissions,
  ]);

  useEffect(() => {
    if (isLoading || listViewedEventTracked.current) {
      return;
    }
    trackEvent("Insight List Viewed", events.listViewedEvent);
    listViewedEventTracked.current = true;
  }, [isLoading, events.listViewedEvent, trackEvent]);

  const handleAddPermissionClicked = (eventType: AddPermissionsEventType) => {
    trackEvent("Insights Add Permissions Clicked", events[eventType]);
  };

  // TODO: Remove the securityHubInsightsNum when we start receiving securityHub insights
  const { permissionsCoverage } = calculatePermissionsMetrics(
    (insights?.length ?? 0) + tempSecurityHubInsightsNum,
    missingPermissionsInsights.length + tempSecurityHubInsightsNum
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
          width: "100%",
          mt: 6,
        }}
      >
        <SyncLoader size={10} color={theme.palette.primary.main} loading />
      </Box>
    );
  }

  if (!insights?.length) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 6, mb: 4 }}>
          {!hideTitle && (
            <Header
              permissionsCoverage={permissionsCoverage}
              onCoverageClick={() => {
                setShowPermissionsModal(true);
              }}
            />
          )}
          <NotFound />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 6, mb: 4 }}>
        {!hideTitle && (
          <Header
            permissionsCoverage={permissionsCoverage}
            onCoverageClick={() => {
              setShowPermissionsModal(true);
            }}
          />
        )}
        <PermissionsSummaryModal
          totalInsightsNum={insights.length}
          permissionsNeededInsights={missingPermissionsInsights}
          permissionsNeededAccounts={{
            [InsightProviderId.TRUSTED_ADVISOR]: accountsWithoutTAPermissions,
            [InsightProviderId.COST_OPTIMIZATION_HUB]: accountsWithoutCostOptPermissions,
            [InsightProviderId.SECURITY_HUB]: accountsWithoutSecurityHubPermissions,
          }}
          hasGKEAccess={hasGKE}
          hasMissingGKEPermissions={gkeAccessType !== InsightAccessType.Full}
          hasMissingCloudConnectPermissions={hasMissingCloudConnectPermissions}
          nonHealthyOrganizationCloudConnects={nonHealthyOrganizationCloudConnectsIds}
          tempSecurityHubInsightsNum={tempSecurityHubInsightsNum}
          errorLoadingAccounts={errorMissingPermissionAccounts}
          isLoading={isLoadingMissingPermissionAccounts || isLoadingCloudConnects || isLoadingSecurityInsights}
          open={showPermissionsModal}
          onClose={() => {
            setShowPermissionsModal(false);
          }}
          onAddPermissionsClicked={handleAddPermissionClicked}
        />
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          {!hideFilters && (
            <FilterSectionsList
              filterConfigs={FILTER_CONFIGS}
              filters={filters}
              newExpandedSections={expandedFilterSections}
              insights={selectedInsightsNoFilter}
              onFilterChange={handleFilterChange}
            />
          )}
          <Box
            sx={{
              width: { xs: "100%", md: 966 },
              flexGrow: 1,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              sx={{
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                mb: 3,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search insights..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                sx={{ flex: 1, minWidth: 250, maxWidth: 500 }}
                data-cy="insights-search-input"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  mt: { xs: 2, md: 0 },
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <FormControl variant="outlined" size="small" sx={{ minWidth: 200, marginLeft: "0!important" }}>
                  <InputLabel>Sort by</InputLabel>
                  <Select label="Sort by" value={sortOrder} onChange={changeSortOrder} data-cy="insights-sort-select">
                    {SORT_LIST.map((item) => (
                      <MenuItem key={`sort-list-${item.value}`} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            {selectedInsights.length ? (
              <Box display="grid" gap={3} width="100%">
                <Stack spacing={3} sx={{ minWidth: 0 }}>
                  {selectedInsights.map((item) => (
                    <div
                      key={getInsightId(item)}
                      ref={(el) => {
                        insightListItemRefs.current[getInsightId(item)] = el;
                      }}
                    >
                      <InsightsListRow insight={item} isClickable={item.displayStatus !== "permissions needed"} />
                    </div>
                  ))}
                </Stack>
              </Box>
            ) : (
              <NotFound message="We couldn't find any relevant insights with the selected filters." showIcon={false} />
            )}
          </Box>
        </Stack>
      </Box>
    </Container>
  );
};
